import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { IssueMediaKind, issueMediaFiles, issueMediaIds, issueMediaKindOf, tagIssueMedia } from '../../../../../../../../../../shared/helpers/issue-media';
import { AppHttpService } from '../../../../../../../../../../shared/services/app-http.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { DailyReportService } from '../../../../daily-report.service';
import { takeUntil } from 'rxjs/operators';
import { allBidItemsFilter, dailyReport, issueTypesFilter, projectDetails } from '../../../../../../../../core/projects.selectors';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
  AddIssuesDetailsRequest,
  FetchAllBidItemsRequest,
  UpdateIssuesDetailsRequest,
} from '../../../../../../../../core/projects.actions';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../../../../shared/services';
import { Ability, subject } from '@casl/ability';
import {
  canEditExistingIssue,
  isIssueWorkflowEnabledForIssue,
  issuePageProjectId,
  IssueWorkflowIssue,
  IssueWorkflowProject,
} from '../../../../../../../../../../shared/helpers/issue-workflow';
import { linkRawValue, patchPickedLocation, recordLinkPayload, recordMarkupKind } from '../../../../../../../../../../shared/helpers/design-context';
import { newIssueDefaultInternal } from '../../../../../../../../../../shared/helpers/issue-defaults';

@Component({
    selector: 'app-issue-details-form',
    templateUrl: './issue-details-form.component.html',
    styleUrls: ['./issue-details-form.component.scss'],
    standalone: false
})
export class IssueDetailsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;
  @Input() isMarkerClick = false;


  form = new FormGroup({
    title: new FormControl({ value: null, disabled: false }),
    description: new FormControl({ value: null, disabled: false }),
    projectScope: new FormControl({ value: null, disabled: false }, { validators: [Validators.required] }),
    bidItem: new FormControl({ value: null, disabled: false }),
    comment: new FormControl({ value: null, disabled: false }),
    type: new FormControl({ value: null, disabled: false }, { validators: [Validators.required] }),
    internal: new FormControl({ value: true, disabled: false }),
    latitude: new FormControl(null),
    longitude: new FormControl(null),
    location: new FormControl(null),
    station_range: new FormControl(null),
    map_feature_link: new FormControl(null),
    pictures: new FormControl('[]'),
    videos: new FormControl('[]'),
    // Documents / other files (the API's `attachments`): kept and saved like
    // pictures, or a save would unlink them.
    attachments: new FormControl('[]'),
  });

  errorMessages: any = {};
  errorMessageMap = {
    bidItem: {
      required: 'This field is required.',
    },
    type: {
      required: 'This field is required.',
    },
    projectScope: {
      required: 'This field is required.',
    },
  };

  formSubscription: Subscription = new Subscription();
  projectScopeOptions: any = [];
  bidItemOptions: any = [];
  projectId: string;
  projectIdFromQp: string;
  pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 10000,
  };
  categoryOptions: any[] = [];
  mediaFiles = [];
  // inject() rather than a constructor param: the repo's lint flags
  // constructor injection, so a new dependency shouldn't add a violation.
  private readonly appHttpService = inject(AppHttpService);
  segment: string;
  videoToBeDeletedFromMuxDb = [];
  selectedBidItemIds: any = [];
  dailyReport: any;
  editable: any;
  /**
   * False when the issue is on the contractor workflow and the user cannot
   * edit it (contractors, viewers, reviewers): there is nothing to save, so
   * the Save bar is hidden. Always true off the workflow.
   */
  @Output() saveBarVisibleChange = new EventEmitter<boolean>();
  /** The project the screen has loaded; decides whether the issue is on the contractor workflow. */
  private loadedProject: IssueWorkflowProject | null = null;
  private readonly authService = inject(AuthService);
  /**
   * The issue as the API returns it on its own, loaded by the workflow panel
   * (so only ever set on a contractor-workflow project). The daily report's
   * copy of an issue has no creator and no scope entity, and the abilities
   * on a workflow project depend on both.
   */
  private workflowIssue: IssueWorkflowIssue | null = null;
  /** The user flipped the Internal toggle on a new issue: the project default no longer applies. */
  private internalTouched = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private dailyReportService: DailyReportService,
    private cdRef: ChangeDetectorRef,
    private ability: Ability,
  ) {
    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(params => {
      if (params && params['projectId']) {
        this.projectIdFromQp = params['projectId'];
      }
    });

    if (!commonService.getCurrentIsMarkerClick()) {
      const urlWithoutQp = this.router.url.split('?')[0];
      const segments = urlWithoutQp.split('/');
      if (urlWithoutQp.includes('issues')) {
        this.segment = segments[3].split('?')[0];
      }
      this.projectId = urlWithoutQp.includes('issues') ? this.segment : segments[2];

      const payload = {
        projectId: this.projectId || this.projectIdFromQp,
        qp: {
          include: [
            {
              bid_items: ['fields'],
            },
          ],
        },
      };

      this.store.dispatch(FetchAllBidItemsRequest({ payload }));

      this.store.select(issueTypesFilter)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(data => {
          if (data.length > 0) {
            this.categoryOptions = data;
          } else {
            this.categoryOptions = [
              {
                value: 'uSZJ8f1DFVpUHjB38x9vEW',
                label: 'Standard',
                id: 'uSZJ8f1DFVpUHjB38x9vEW',
                type: 'standard',
              },
              {
                value: '8EUNXG7Xfq1Mp1Tn13pki4',
                label: 'Environment',
                id: '8EUNXG7Xfq1Mp1Tn13pki4',
                type: 'environment',
              },
              {
                value: 'oYGCxpAmv8Viev4BA3Mp83',
                label: 'Safety',
                id: 'oYGCxpAmv8Viev4BA3Mp83',
                type: 'safety',
              },
              {
                value: '8ZBH7RcGikKitWkJ3DfgMG',
                label: 'As built',
                id: '8ZBH7RcGikKitWkJ3DfgMG',
                type: 'as-built',
              },
            ];
          }
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isMarkerClick']) {
      this.updateFormDisabledState();
    }
  }

  private prefillMapFeatureIssue(): void {
    // A map feature needs the issue's own pin; a sheet-only link (the Drawings viewer's Add to) has none.
    const sheetOnly = !!this.data?.map_feature_link?.sheet && !this.data?.map_feature_ref && !this.data?.map_feature_link?.map_feature_ref;
    const hasPin = Number.isFinite(this.data?.latitude) && Number.isFinite(this.data?.longitude);
    if (this.data?.id || (!this.data?.map_feature_ref && !this.data?.map_feature_link) || this.data.entity_type !== 'project'
      || this.data.entity_id !== this.data.projectId || (!hasPin && !sheetOnly)) return;
    // Project-map drawers can open without the daily-report project selector being loaded.
    this.projectScopeOptions = [...(this.data.issuesScopeOptions || [])];
    if (!this.projectScopeOptions.some(option => option.value === this.data.projectId)) {
      this.projectScopeOptions.unshift({ value: this.data.projectId, label: 'Current project', type: 'project' });
    }
    this.form.patchValue({ ...(hasPin ? { latitude: this.data.latitude, longitude: this.data.longitude } : {}),
      projectScope: this.data.entity_id, map_feature_link: this.data.map_feature_link ?? null }, { emitEvent: false });
  }

  /** 7.2: an issue in an as-built category marks up its sheet as As-built by default */
  get markupKind(): 'note' | 'as_built' {
    const type = this.form?.get('type')?.value;
    const option = this.categoryOptions.find(o => o.value === type || o.id === type);
    return recordMarkupKind(option?.label ?? (typeof type === 'string' ? type : null));
  }

  ngOnInit() {
    this.store.select(allBidItemsFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((bidItems) => {
        this.bidItemOptions = bidItems;
      });

    if (this.data?.id) {
      const initialScopeFromEntity = this.data?.entity?.id ?? this.data?.entity_id;
      const initialScopeFromType = this.projectScopeOptions
        ?.find(i => i.type === this.data?.entity_type);
      this.form.patchValue({
        title: this.data?.title,
        description: this.data?.description,
        type: this.data?.type,
        internal: this.data?.internal,
        projectScope: initialScopeFromEntity ?? initialScopeFromType?.id ?? initialScopeFromType?.value,
        bidItem: this.data?.bid_items?.map(o => o?.id),
        latitude: this.data?.latitude,
        longitude: this.data?.longitude,
        location: this.data?.location,
        station_range: this.data?.station_range,
        map_feature_link: this.data?.map_feature_link ?? null,
        pictures: JSON.stringify(this.data?.pictures || []),
        videos: JSON.stringify(this.data?.videos || []),
        attachments: JSON.stringify(this.data?.attachments || []),
        comment: this.data.comment,
      }, { emitEvent: false });
      // Every kind of file, each tagged for the MEDIA section (it draws a file
      // by its fileType and shows nothing for an untagged one).
      this.mediaFiles = issueMediaFiles(this.data);
      this.loadMissingMedia();
      this.dailyReportService.hideDeleteOption.next(true);
      this.dailyReportService.issue.next(this.data);
    } else {
      this.prefillMapFeatureIssue();
      this.applyNewIssueInternalDefault();
      this.dailyReportService.disableOptionMenu.next(true);
    }

    this.form.controls?.['bidItem']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.selectedBidItemIds = [];

        if (data?.length > 0) {
          this.selectedBidItemIds = this.bidItemOptions.filter(item => data?.includes(item?.id));
        }
      });

    this.dailyReportService.fetchComments
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        const closingComments = this.form.controls['comment']?.value;
        this.dailyReportService.commentsValue.next(closingComments);
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.save();
      });

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];
        // A tagged upload is an attachment of a contractor-workflow action
        // (the panel next to this form, on workflow projects only), not media
        // of the issue. Uploads made for the issue itself carry no tag.
        if (items.length && items.every((eachItem: { tags?: string[] }) => eachItem?.tags?.length)) {
          return;
        }

        items.forEach((eachItem: any) => {
          // Pictures, videos, and documents (saved as the issue's attachments,
          // the list mobile files them in).
          const kind = issueMediaKindOf(eachItem?.['fileType']);
          if (!kind) {
            return;
          }
          const list = JSON.parse(this.form.get(kind)?.value || '[]');
          list.push(eachItem);
          this.form?.get(kind)?.setValue(JSON.stringify(list), { emitEvent: false });
          this.mediaFiles.push(eachItem);
        });
        this.mediaFiles = [...this.mediaFiles];
        this.projectsService.isAnyFormDirty.next(true);
      });

    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe((location) => {
        patchPickedLocation(this.form, location, { emitEvent: false });
        this.projectsService.isAnyFormDirty.next(true);
        this.cdRef.detectChanges();
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        // Kept through a refetch (the store holds nothing while one is in flight).
        if (data?.id) {
          this.loadedProject = data;
          this.applyNewIssueInternalDefault();
        }
        if ((this.data?.map_feature_ref || this.data?.map_feature_link) && (!data?.id || (this.data.projectId && data.id !== this.data.projectId))) return;
        this.projectScopeOptions = [
          {
            value: `${data?.id}`,
            label: `Project ${data?.name}`,
            type: 'project',
          },
          ...data?.sites.map(o => ({
            value: `${o.id}`,
            label: `Site ${o.name}`,
            type: 'site',
          })),
        ];

        // If the loaded issue has entity_type but no entity_id/entity, infer
        // the scope from projectScopeOptions so the form (and any save) has
        // a valid value.
        const currentScope = this.form.controls?.projectScope?.value;
        if (!currentScope && this.data?.entity_type) {
          const match = this.projectScopeOptions.find(o => o.type === this.data?.entity_type);
          if (match) {
            this.form.controls.projectScope.setValue(match.value, { emitEvent: false });
          }
        }

        // Recompute editability now that scope options are available — the
        // CASL update permission for site-scoped issues needs site_id, which
        // we resolve via projectScopeOptions when the issue data lacks
        // entity_id.
        this.recomputeEditability();
      });

    this.formSubscription = this.form.valueChanges
      .pipe(
        takeUntil(this.onDestroy),
      )
      .subscribe(() => {
        if (this.editable && !this.isMarkerClick) {
          this.projectsService.isAnyFormDirty.next(true);
        }
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.dailyReport = { ...report };
        this.recomputeEditability();
      });
  }

  private recomputeEditability() {
    if (this.data?.id) {
      const issueData = { ...this.withWorkflowIssue(this.data) };
      if (issueData.entity_type === 'site') {
        const scopeOptionByType = this.projectScopeOptions
          ?.find(o => o.type === 'site');
        const siteId = issueData.entity?.id
          ?? issueData.entity_id
          ?? scopeOptionByType?.id
          ?? scopeOptionByType?.value;
        if (siteId) {
          issueData.site_id = siteId;
        }
      }
      if (!issueData.project_id) {
        const projectScopeOption = this.projectScopeOptions
          ?.find(o => o.type === 'project');
        issueData.project_id = this.data?.projectId
          ?? this.data?.project_id
          ?? this.data?.project?.id
          ?? projectScopeOption?.value;
      }
      const issueSubject = subject('Issue', issueData);
      // Off the contractor workflow this is the legacy check (update or
      // create). On a workflow project `create` no longer implies editing:
      // a contractor PM raises issues but edits only their own.
      this.editable = canEditExistingIssue(
        this.isWorkflowIssue(),
        this.ability.can('update', issueSubject),
        this.ability.can('create', issueSubject),
      );
    } else {
      this.editable = this.ability.can('create', 'Issue');
    }

    if (!this.editable) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
    const saveBarVisible = !!this.editable || !this.isWorkflowIssue();
    // Deferred: editability can change while the parent is being checked.
    Promise.resolve().then(() => this.saveBarVisibleChange.emit(saveBarVisible));
  }

  /**
   * A contractor-workflow action changed the issue behind the open form:
   * sending it to a contractor makes it public.
   */
  applyWorkflowChange(issue: IssueWorkflowIssue) {
    if (typeof issue?.internal === 'boolean') {
      this.form.controls.internal.setValue(issue.internal, { emitEvent: false });
    }
    // The Save bar follows the issue it was given (status, editability).
    this.dailyReportService.issue.next(this.withWorkflowIssue(issue));
  }

  /**
   * The workflow panel loaded the issue from the API (contractor-workflow
   * projects only): editability is decided again with its creator and scope.
   */
  applyWorkflowIssue(issue: IssueWorkflowIssue) {
    this.workflowIssue = issue;
    this.recomputeEditability();
    this.dailyReportService.issue.next(this.withWorkflowIssue(this.data));
  }

  /**
   * A contractor on the issue's project: the Internal flag is not shown to
   * them. Everyone else sees it; whether they can change it (PM, project
   * owner, ...) is the form's editability, i.e. the API's `update` ability,
   * not a role list here.
   */
  get isContractor(): boolean {
    const projectId = this.loadedProject?.id ?? issuePageProjectId(this.router.url) ?? this.data?.project_id;
    const account: any = this.authService.getCurrentUser();
    const role = account?.roles?.find((r: any) => r?.project_id === projectId)?.role || '';
    return role.startsWith('contractor');
  }

  /**
   * Whether the open issue is on a contractor-workflow project. The daily
   * report's issues do not say which project they are in: the page does.
   */
  private isWorkflowIssue(): boolean {
    return isIssueWorkflowEnabledForIssue(this.data, this.loadedProject, issuePageProjectId(this.router.url));
  }

  /** `data` with the creator and scope filled in from the API's copy, where it lacks them. */
  private withWorkflowIssue<T extends IssueWorkflowIssue>(data: T): T {
    if (!this.workflowIssue || !data) {
      return data;
    }
    return {
      ...data,
      createdBy: data.createdBy ?? this.workflowIssue.createdBy,
      entity: data.entity ?? this.workflowIssue.entity,
    };
  }

  save() {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    // A link removed this session is { sheet: null }; an untouched null stays a bare null (R54)
    const values = linkRawValue(this.form);
    const { pictures, videos, attachments, bidItem, projectScope, map_feature_link, ...rest } = values;
    const tempPictureData = issueMediaIds(pictures);
    const tempVideoData = issueMediaIds(videos);
    // Always sent: the API relinks exactly the ids it gets, so leaving them
    // out would unlink the issue's documents (and the record's own objects
    // spread in from `data` fail its id-list validation).
    const tempAttachmentData = issueMediaIds(attachments);
    const entityType = this.projectScopeOptions?.find(i => (i.id || i.value) === projectScope);
    const dataEntityType = this.data?.entity_type;
    const scopeOptionByType = this.projectScopeOptions?.find(i => i.type === dataEntityType);
    const resolvedEntityId = projectScope
      ?? this.data?.entity_id
      ?? this.data?.entity?.id
      ?? (scopeOptionByType?.id || scopeOptionByType?.value);
    const resolvedEntityType = entityType?.type ?? dataEntityType ?? scopeOptionByType?.type;

    // map_feature_ref (the map hand-off's prefill guard) and the serialized link never reach the api:
    // the link is sent as the payload shape built from the form control.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { issuesScopeOptions, projectId, map_feature_ref, map_feature_link: savedLink, ...issueRest } = this.data || {};

    const payload = {
      ...issueRest,
      ...rest,
      pictures: tempPictureData,
      videos: tempVideoData,
      attachments: tempAttachmentData,
      bid_item_ids: bidItem,
      entity_id: resolvedEntityId,
      entity_type: resolvedEntityType,
      // A pinless record never carries a map feature, but keeps a sheet target (recordLinkPayload). An issue that never
      // had a link sends a plain null, as before: `{ sheet: null }` would put it through the api's link ACL.
      map_feature_link: !map_feature_link && !savedLink ? null : recordLinkPayload(map_feature_link ?? null, rest.latitude, rest.longitude),
    };

    if (payload.id) {
      payload.qp = {
        include: [
          'createdBy',
          'bid_item',
          'bid_items',
          'entity',
          'pictures',
          'videos',
          'attachments',
          'material_tests',
          'map_feature_link',
        ],
      };
      this.store.dispatch(UpdateIssuesDetailsRequest({ payload }));
    } else {
      this.store.dispatch(AddIssuesDetailsRequest({ payload }));
    }
  }

  onToggle(event: MatSlideToggleChange) {
    this.internalTouched = true;
    this.form.controls['internal'].setValue(event.checked);
  }

  /**
   * A new issue starts with its project's Internal default
   * (source_entity_extra_attributes.issue_default_internal, Internal unless
   * the project sets it to false). Read from the issue's own project only:
   * the map hand-off names it, otherwise the page's (the site's) project.
   * Never touches an existing issue or a value the user already picked.
   */
  private applyNewIssueInternalDefault(): void {
    if (this.data?.id || this.internalTouched) {
      return;
    }
    const issueProjectId = this.data?.projectId ?? issuePageProjectId(this.router.url) ?? this.projectId ?? this.projectIdFromQp;
    this.form.controls.internal.setValue(newIssueDefaultInternal(this.loadedProject, issueProjectId), { emitEvent: false });
  }

  /**
   * Some panels open on a partial issue (a map marker's data) that lacks one
   * of the media lists. Those are read from the full issue, so the MEDIA
   * section shows every file and a save doesn't send an empty list for one
   * the form never saw (the API relinks exactly what it gets).
   */
  private loadMissingMedia(): void {
    const kinds: IssueMediaKind[] = ['pictures', 'videos', 'attachments'];
    const missing = kinds.filter(kind => !Array.isArray(this.data?.[kind]));
    if (!this.data?.id || !missing.length) {
      return;
    }
    this.appHttpService.getService(`/v1/issue/${this.data.id}`)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: resp => {
          const full = resp?.data || {};
          missing.forEach(kind => {
            const files = tagIssueMedia(full[kind], kind);
            // Keep anything uploaded meanwhile.
            const current = JSON.parse(this.form.get(kind)?.value || '[]');
            const known = new Set(current.map(f => f?.id));
            const merged = [...files.filter(f => !known.has(f?.id)), ...current];
            this.form.get(kind)?.setValue(JSON.stringify(merged), { emitEvent: false });
            this.mediaFiles = [...this.mediaFiles, ...files.filter(f => !known.has(f?.id))];
          });
          this.cdRef.detectChanges();
        },
        error: () => {
          // The issue can't be read: the panel shows what it was given.
        },
      });
  }

  /**
   * Removes a file from the MEDIA section and from whichever list holds it
   * (a document can sit in pictures as well as attachments).
   */
  deletePicture(pic) {
    this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);

    (['pictures', 'videos', 'attachments'] as const).forEach(kind => {
      const list = JSON.parse(this.form.get(kind)?.value || '[]');
      const kept = list.filter(o => o?.id !== pic.id);
      if (kept.length !== list.length) {
        this.form?.get(kind)?.setValue(JSON.stringify(kept));
      }
    });

    if (pic.fileType === 'video') {
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  deleteLocation() {
    this.form?.get('latitude').setValue(null);
    this.form?.get('longitude').setValue(null);
    this.form?.get('location').setValue(null);
    this.form?.get('station_range').setValue(null);
    // The link goes with the location: an explicit removal (R54)
    if (this.form?.get('map_feature_link')?.value) this.form.get('map_feature_link').markAsDirty();
    this.form?.get('map_feature_link').setValue(null);
  }

  showLocationSection() {
    const hasGps = this.form?.controls?.latitude?.value !== null &&
      this.form?.controls?.longitude?.value !== null &&
      this.form?.controls?.latitude?.value !== '' &&
      this.form?.controls?.longitude?.value !== '';

    // A sheet-only link (the Drawings viewer's Add to) has no pin but still shows its card.
    return hasGps || !!this.form?.controls?.location?.value || !!this.form?.controls?.station_range?.value
      || !!this.form?.controls?.map_feature_link?.value?.sheet;
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.commonService.updateIsMarkerClick(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  private updateFormDisabledState() {
    if (this.isMarkerClick) {
      this.form.disable({ emitEvent: false }); // Disable the entire form
    } else {
      this.form.enable({ emitEvent: false }); // Enable the entire form
    }
  }
}
